import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Clock, User, Check, MessageCircle, Sparkles, Phone, Facebook, CreditCard, Crown } from "lucide-react";
import { FACEBOOK_URL, MESSENGER_URL, BOOKING_URL } from "@/constants/content";
import { useSiteContent } from "@/hooks/useSiteContent";
import { MASSAGE } from "@/constants/testIds";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

function MassageCard({ s, i }) {
  const [vipTier, setVipTier] = useState("vip4");
  const hasVip8 = !!s.vip8Price;
  const vipPrice = vipTier === "vip8" && hasVip8 ? s.vip8Price : s.vip4Price;
  const vipLabel = vipTier === "vip8" ? "Havi 8 alkalom (Heti 2x)" : "Havi 4 alkalom";
  const vipOnly = !!s.vipOnly;

  return (
    <motion.article
      key={s.key}
      id={s.key}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: i * 0.08 }}
      data-testid={MASSAGE.card(s.key)}
      className="relative bg-white rounded-[2rem] border border-[#EAE5D9] overflow-hidden flex flex-col shadow-[0_8px_32px_rgba(26,40,37,0.04)]"
    >
      <div
        className="h-2"
        style={{ background: `linear-gradient(90deg, ${s.accent}, ${s.accent}80)` }}
      />
      <div className="p-7 md:p-8 flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-[0.22em] text-[#758C88]">
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border"
            style={{ borderColor: `${s.accent}40`, color: s.accent }}
          >
            <User className="h-3 w-3" /> {s.ageGroup || "Felnőtt"}
          </span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {s.duration}</span>
        </div>
        <h2 className="font-serif-display text-2xl md:text-[26px] text-[#1A2825] leading-snug mt-3">
          {s.title}
        </h2>
        <p className="mt-1.5 text-sm italic" style={{ color: s.accent }}>
          {s.short}
        </p>

        {/* Description */}
        <p className="mt-4 text-[#3A4D4A] font-light leading-relaxed text-[15px]">
          {s.description}
        </p>
        <ul className="mt-4 space-y-2 text-sm text-[#3A4D4A] font-light">
          {(s.benefits || []).map((b) => (
            <li key={b} className="flex items-start gap-2">
              <Check className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: s.accent }} />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        {/* Prices */}
        {(s.prices && s.prices.length > 0) && (
          <div
            data-testid={`massage-${s.key}-prices`}
            className="mt-6 pt-5 border-t border-[#EAE5D9] space-y-2"
          >
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#A88A2B] mb-1">
              Sima árak
            </div>
            {s.prices.map((p) => (
              <div key={p.label} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-[#3A4D4A] font-light">{p.label}</span>
                <span className="font-medium text-[#1A2825]">{p.price}</span>
              </div>
            ))}
          </div>
        )}

        {/* VIP block */}
        {(s.vip4Price || s.vip8Price) && (
          <div
            data-testid={`massage-${s.key}-vip`}
            className="mt-5 rounded-2xl border p-5"
            style={{
              background: `${s.accent}0D`,
              borderColor: `${s.accent}30`,
            }}
          >
            <div
              className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] mb-2"
              style={{ color: s.accent }}
            >
              <Crown className="h-3.5 w-3.5" />
              {vipOnly ? "Kizárólag VIP Tagsággal" : "VIP Tagság"}
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm text-[#3A4D4A] font-light">{vipLabel}</span>
              <span
                className="font-serif-display text-xl md:text-2xl leading-none"
                style={{ color: s.accent }}
              >
                {vipPrice}
              </span>
            </div>

            {hasVip8 && (
              <div className="mt-3 grid grid-cols-2 gap-1 p-1 bg-white/70 rounded-full">
                <button
                  type="button"
                  onClick={() => setVipTier("vip4")}
                  data-testid={`massage-${s.key}-vip4-btn`}
                  className={`text-[11px] py-1.5 px-2 rounded-full transition-all ${
                    vipTier === "vip4"
                      ? "bg-white text-[#1A2825] shadow-sm font-medium"
                      : "text-[#758C88] hover:text-[#1A2825]"
                  }`}
                >
                  Havi 4×
                </button>
                <button
                  type="button"
                  onClick={() => setVipTier("vip8")}
                  data-testid={`massage-${s.key}-vip8-btn`}
                  className={`text-[11px] py-1.5 px-2 rounded-full transition-all ${
                    vipTier === "vip8"
                      ? "bg-white text-[#1A2825] shadow-sm font-medium"
                      : "text-[#758C88] hover:text-[#1A2825]"
                  }`}
                >
                  Havi 8× (Heti 2×)
                </button>
              </div>
            )}
          </div>
        )}

        {/* Buttons */}
        <div className="mt-6 flex flex-col gap-2.5">
          {(s.vip4Price || s.vip8Price) && (
            <a
              href={`${MESSENGER_URL}?ref=vip-${s.key}-${vipTier}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`massage-${s.key}-subscribe-btn`}
              className="inline-flex items-center justify-center gap-2 text-white px-6 py-3 rounded-full font-medium tracking-wide transition-all shadow-md hover:-translate-y-0.5"
              style={{ backgroundColor: s.accent }}
            >
              <CreditCard className="h-4 w-4" />
              Előfizetek a Havi Tagságra
            </a>
          )}
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={MASSAGE.bookBtn(s.key)}
            className="inline-flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#0e5fcc] text-white px-6 py-3 rounded-full font-medium tracking-wide transition-all shadow-md hover:-translate-y-0.5"
          >
            <MessageCircle className="h-4 w-4" />
            Időpontot foglalok
          </a>
          {(s.vip4Price || s.vip8Price) && (
            <p className="text-[11px] text-center text-[#758C88] italic mt-1">
              Online fizetés hamarosan – addig Messengeren egyeztetjük ✨
            </p>
          )}
        </div>
      </div>
    </motion.article>
  );
}

export default function Masszazs() {
  const { content } = useSiteContent();
  const MASSAGES = content.massages;
  return (
    <div data-testid={MASSAGE.page}>
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-10 md:pb-14">
        <motion.div
          initial="hidden" animate="show" variants={fadeUp}
          className="max-w-3xl"
        >
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#A88A2B]">Masszázs & Wellness</span>
          <h1 className="font-serif-display text-5xl md:text-6xl font-light text-[#1A2825] mt-4 leading-[1.04]">
            Kezelések — a Te <em className="not-italic text-[#2A9D8F]">tempódra</em> szabva.
          </h1>
          <p className="mt-6 text-lg text-[#3A4D4A] font-light leading-relaxed">
            Klasszikus svéd alapokra épülő, de mindig személyre szabott munka. Válaszd az alkalmi
            árat vagy a <strong className="text-[#A88A2B] font-medium">VIP Havi Tagságot</strong>,
            amellyel akár 4× vagy heti 2× élvezheted a kezelést – kedvezőbb áron.
          </p>
        </motion.div>
      </section>

      {/* Cards */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {MASSAGES.map((s, i) => (
            <MassageCard key={s.key} s={s} i={i} />
          ))}
        </div>

        {/* FB note */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-10 md:mt-14 flex flex-col sm:flex-row items-center justify-center gap-4 text-center"
        >
          <p className="text-sm md:text-base text-[#3A4D4A] italic font-light">
            További kezeléseinkről és árainkról bővebb infó a Facebook oldalon található.
          </p>
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#1877F2] hover:bg-[#0e5fcc] text-white px-5 py-2.5 rounded-full text-sm font-medium tracking-wide transition-all shadow-[0_6px_18px_rgba(24,119,242,0.25)] hover:-translate-y-0.5"
          >
            <Facebook className="h-4 w-4" /> Facebook oldal
          </a>
        </motion.div>
      </section>

      {/* Hogyan működik a VIP Tagság */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-10 md:mb-14"
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A88A2B] inline-flex items-center gap-2">
            <Crown className="h-3.5 w-3.5" /> VIP Havi Tagság
          </div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A2825] mt-3 leading-tight">
            Így működik a <em className="not-italic text-[#2A9D8F]">Havi Tagság</em>
          </h2>
          <p className="mt-4 text-[#3A4D4A] font-light leading-relaxed">
            Egyszerű. Rugalmas. És sokkal kedvezőbb, mintha alkalmanként fizetnél.
            A Havi Tagságoddal fix helyed van nálunk – hó közben.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {[
            {
              n: "01",
              icon: MessageCircle,
              title: "Válaszd ki a kezelést",
              text: "Nézd át a 6 kezelést, gondold végig mennyi feltöltődésre van szükséged: Havi 4× vagy Heti 2×. Írj Messengeren, egyeztetjük.",
              accent: "#2A9D8F",
            },
            {
              n: "02",
              icon: CreditCard,
              title: "Egyeztess Messengeren",
              text: "Válaszolj pár rövid kérdésre (mikor kényelmes, milyen gyakorisággal), és megbeszéljük a részleteket. Az online kártyás fizetés hamarosan elérhető – addig egyszerűen intézzük.",
              accent: "#A88A2B",
            },
            {
              n: "03",
              icon: Sparkles,
              title: "A hónap folyamán élvezed",
              text: "Havi 4× vagy Heti 2× jössz be, ahogy a naptárad engedi. Cél: rendszeres, tudatos feltöltődés – nem alkalmi luxus.",
              accent: "#1C6B61",
            },
            {
              n: "04",
              icon: Crown,
              title: "Nincs kényszer, bármikor módosíthatsz",
              text: "Nem elköteleződés életre. Ha egyik hó kimarad, szólj – vagy váltasz Havi 8×-re / Havi 4×-re. Rugalmas, akárcsak a testünk.",
              accent: "#D4AF37",
            },
          ].map((step, i) => (
            <motion.div
              key={step.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`vip-step-${step.n}`}
              className="relative bg-white rounded-[1.75rem] border border-[#EAE5D9] p-6 md:p-7 flex flex-col shadow-[0_6px_24px_rgba(26,40,37,0.04)]"
            >
              <div className="flex items-center justify-between mb-4">
                <span
                  className="font-serif-display text-3xl md:text-4xl leading-none"
                  style={{ color: step.accent }}
                >
                  {step.n}
                </span>
                <div
                  className="h-10 w-10 rounded-2xl flex items-center justify-center"
                  style={{ background: `${step.accent}12`, color: step.accent }}
                >
                  <step.icon className="h-5 w-5" />
                </div>
              </div>
              <h3 className="font-serif-display text-lg md:text-xl text-[#1A2825] leading-snug">
                {step.title}
              </h3>
              <p className="mt-3 text-sm text-[#3A4D4A] font-light leading-relaxed">
                {step.text}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-8 text-center max-w-2xl mx-auto"
        >
          <p className="text-sm text-[#3A4D4A] font-light italic">
            „A rendszeres feltöltődés nem luxus – hanem szeretet, amit magadnak adsz."
          </p>
          <a
            href={MESSENGER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="vip-messenger-cta"
            className="mt-5 inline-flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#0e5fcc] text-white px-6 py-3 rounded-full font-medium tracking-wide transition-all shadow-md hover:-translate-y-0.5"
          >
            <MessageCircle className="h-4 w-4" /> Kérdésem van – Messenger
          </a>
        </motion.div>
      </section>

      {/* CTA strip */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <div className="relative bg-[#F6F3EB]/80 border border-[#D4AF37]/30 rounded-[2.5rem] p-10 md:p-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#A88A2B] flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> Bejelentkezés
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A2825] mt-3 leading-tight">
              Foglalj online, vagy írj nekünk!
            </h2>
            <p className="mt-3 text-[#3A4D4A] font-light leading-relaxed max-w-2xl">
              Az időpontfoglalás közvetlenül az online foglaló felületen a leggyorsabb. Első
              kezelés előtt mindig egyeztetünk, hogy biztosan azt kapd, amire most szükséged van.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-sm text-[#A88A2B]">
              <Phone className="h-4 w-4" /> Online foglaló, telefon és Messenger elérhető
            </div>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#2A9D8F] hover:bg-[#22857a] text-white px-6 py-3.5 rounded-full font-medium tracking-wide transition-all shadow-[0_10px_24px_rgba(42,157,143,0.32)]"
            >
              <MessageCircle className="h-4 w-4" /> Időpontot foglalok
            </a>
            <a
              href={MESSENGER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#0e5fcc] text-white px-6 py-3.5 rounded-full font-medium tracking-wide transition-all"
            >
              <MessageCircle className="h-4 w-4" /> Üzenek Messengeren
            </a>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white border border-[#1877F2]/30 text-[#1877F2] px-6 py-3.5 rounded-full font-medium tracking-wide transition-all hover:border-[#1877F2]"
            >
              Megnézem a Facebook oldalt
            </a>
            <Link
              to="/#magnetek"
              className="inline-flex items-center justify-center gap-2 text-sm text-[#3A4D4A] hover:text-[#2A9D8F]"
            >
              Vagy kérek egy ingyenes ajándékot →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
